import type { CookieConsent } from '@/scopes/common/infrastructure/cookie-consent/types'
import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import type { RoomInfo } from '@/scopes/prices/domain/rooms/types'
import type {
  AbandonedBasketValues,
  BookingDraftPayload,
  BookingDraftResponse,
  ParticipantGroup,
} from '@/scopes/prices/infrastructure/booking-draft/types'
import defaultFetch from 'isomorphic-unfetch'
import cookieClient from '@/scopes/common/infrastructure/cookies/cookieClient'
import { createRoomsParticipantDistributionMapper } from '@/scopes/search/search/infrastructure/api/filtersSelectionMapper/mappers/roomsParticipantDistributionMapper'

interface Dependencies {
  roomsParticipantDistributionMapper: ReturnType<typeof createRoomsParticipantDistributionMapper>
  cookieClient: typeof cookieClient
  fetch: typeof defaultFetch
  env: {
    NEXT_PUBLIC_API_URL: string
  }
  storage: Pick<Storage, 'getItem'>
}

interface BookingDraftContext {
  accoId: string
  contextItemId: string
  url: string
  isFlightOnly: boolean
  cookieConsent: CookieConsent
}

interface BookingDraftFilters {
  selectedPriceId: string
  selectedRooms: RoomInfo[]
  inboundFlight?: string | null
  outboundFlight?: string | null
  inboundBus?: string | null
  outboundBus?: string | null
}

export function createBookingDraftAPIClient(dependencies: Partial<Dependencies>) {
  const {
    roomsParticipantDistributionMapper = createRoomsParticipantDistributionMapper(),
    cookieClient: injectedCookieClient = cookieClient,
    fetch = defaultFetch,
    env = {
      NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    },
    storage = globalThis.localStorage,
  } = dependencies

  async function createBookingDraft(
    criteria: BookingDraftContext & { filterSelection: FilterSelection } & BookingDraftFilters,
  ) {
    const body = buildBookingDraftPayload(criteria)
    const url = `${env.NEXT_PUBLIC_API_URL}/BookingDraft/CreateBookingDraftApi`
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      referrerPolicy: 'no-referrer-when-downgrade',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    })
    const data = (await response.json()) as BookingDraftResponse
    return data
  }

  function buildBookingDraftPayload(
    criteria: BookingDraftContext & { filterSelection: FilterSelection } & BookingDraftFilters,
  ): BookingDraftPayload {
    const duration = criteria.selectedPriceId?.split('|')[0]
    const departureDate = criteria.selectedPriceId?.split('|')[1]

    const mappedParticipants = roomsParticipantDistributionMapper(criteria.filterSelection.Participants ?? [])
    let participantsGroups: ParticipantGroup[]

    if (criteria.isFlightOnly) {
      participantsGroups = [
        {
          participants: mappedParticipants.flat(),
          room: '1XXX',
        },
      ]
    }
    else {
      participantsGroups = criteria.selectedRooms.map((room, index) => ({
        participants: mappedParticipants[index],
        room: room.id,
        roomContractId: room.contractId,
      }))
    }

    return {
      accommodationId: Number(criteria.accoId),
      abandonedBasketValues: buildAbandonedBasketValues(storage, injectedCookieClient),
      cookieConsent: criteria.cookieConsent,
      contextitemid: criteria.contextItemId,
      departureDate,
      duration,
      transportType: criteria.filterSelection.TransportType || '',
      mealplan: criteria.filterSelection.Mealplan || '',
      inboundFlight: criteria.inboundFlight,
      outboundFlight: criteria.outboundFlight,
      inboundBus: criteria.inboundBus,
      outboundBus: criteria.outboundBus,
      participantsGroups,
      referrerUrl: criteria.url,
    }
  }

  return {
    buildBookingDraftPayload,
    createBookingDraft,
  }
}

function buildAbandonedBasketValues(
  storage: Pick<Storage, 'getItem'>,
  cookieClient: { get: (name: string) => string | undefined },
): AbandonedBasketValues {
  return {
    loggedOutMyAccountId: storage.getItem('my_account_id') ?? '',
    SelligentUpi: cookieClient.get('smc_upi') ?? '',
  }
}
